import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faClock,
  faPause,
  faPlay,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import webVulnScanService from '../../services/webVulnScanService';
import ContextMenu from './ContextMenu';
import './ScanUrlTable.css';

// Colors for the Burp-style phase dots.
const PHASE_COLORS = {
  pending: { bg: '#4b556333', fg: '#9ca3af', border: '#6b7280' },
  running: { bg: '#3b82f6', fg: '#ffffff', border: '#3b82f6' },
  done: { bg: '#22c55e', fg: '#ffffff', border: '#22c55e' },
  skipped: { bg: '#37415133', fg: '#6b7280', border: '#4b5563' },
  error: { bg: '#ef4444', fg: '#ffffff', border: '#ef4444' }
};

const ScanUrlTable = ({ scan }) => {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const [urlDetails, setUrlDetails] = useState(scan?.urlDetails || []);
  const [contextMenu, setContextMenu] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const scanActive = scan?.status === 'running' || scan?.status === 'paused';

  // Per-URL control (Burp-style right-click).
  const controlUrl = async (urlIndex, action) => {
    setContextMenu(null);
    // Optimistic status so the row reacts instantly; the WS url_update confirms.
    const optimistic = action === 'pause' ? 'paused' : action === 'cancel' ? 'cancelled' : 'pending';
    setUrlDetails(prev => {
      const next = [...prev];
      if (next[urlIndex]) next[urlIndex] = { ...next[urlIndex], status: optimistic };
      return next;
    });
    try {
      await webVulnScanService.controlScanUrl(scan._id, urlIndex, action);
    } catch (err) {
      toast.error(`Failed to ${action} URL: ${err.response?.data?.message || err.message}`);
    }
  };

  const openRowMenu = (e, urlDetail, index) => {
    if (!scanActive) return; // only for running/paused scans
    e.preventDefault();
    const st = urlDetail.status;
    const items = [];
    if (st === 'testing' || st === 'pending') {
      items.push({ label: 'Pause this URL', icon: faPause, onClick: () => controlUrl(index, 'pause') });
    }
    if (st === 'paused') {
      items.push({ label: 'Resume this URL', icon: faPlay, onClick: () => controlUrl(index, 'resume') });
    }
    if (['testing', 'pending', 'paused'].includes(st)) {
      items.push({ label: 'Cancel this URL', icon: faBan, danger: true, onClick: () => controlUrl(index, 'cancel') });
    }
    if (items.length === 0) return; // finished URLs have no controls
    setContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
  };

  // WebSocket connection
  useEffect(() => {
    if (!scan || !scan._id || !user) return;

    const connectWebSocket = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

      // Build WebSocket URL based on environment
      let wsUrl;
      if (window.location.hostname === 'localhost') {
        // Development: explicitly use port 5000
        wsUrl = `${protocol}//localhost:5000/ws/scanner`;
      } else {
        // Production: use current host (nginx proxy handles it)
        wsUrl = `${protocol}//${window.location.host}/ws/scanner`;
      }

      console.log('[Scanner WS] Connecting to:', wsUrl);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[Scanner WS] Connected');

        // Authenticate
        ws.send(JSON.stringify({
          type: 'auth',
          userId: user._id,
          scanId: scan._id,
          tenantId: user.tenant
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[Scanner WS] Received:', message);

          if (message.type === 'url_update' && message.scanId === scan._id) {
            handleUrlUpdate(message.data);
          } else if (message.type === 'finding_discovered' && message.scanId === scan._id) {
            handleFindingDiscovered(message.data);
          }
        } catch (error) {
          console.error('[Scanner WS] Error processing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[Scanner WS] Error:', error);
      };

      ws.onclose = () => {
        console.log('[Scanner WS] Disconnected');
        wsRef.current = null;

        // Reconnect after 3 seconds if scan is still running
        if (scan.status === 'running') {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
        }
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [scan?._id, scan?.status, user]);

  // Update URL details from WebSocket
  const handleUrlUpdate = (data) => {
    const { urlIndex, ...updateData } = data;

    setUrlDetails(prev => {
      const updated = [...prev];
      if (urlIndex >= 0 && urlIndex < updated.length) {
        updated[urlIndex] = {
          ...updated[urlIndex],
          ...updateData
        };
      }
      return updated;
    });
  };

  // Handle finding discovered
  const handleFindingDiscovered = (data) => {
    const { urlIndex } = data;

    setUrlDetails(prev => {
      const updated = [...prev];
      if (urlIndex >= 0 && urlIndex < updated.length) {
        // Update will come via url_update message
      }
      return updated;
    });
  };

  // Update local state when scan prop changes
  useEffect(() => {
    if (scan?.urlDetails) {
      setUrlDetails(scan.urlDetails);
    }
  }, [scan?.urlDetails]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'testing':
        return <FontAwesomeIcon icon={faSpinner} spin className="status-icon testing" />;
      case 'completed':
        return <FontAwesomeIcon icon={faCheckCircle} className="status-icon completed" />;
      case 'error':
        return <FontAwesomeIcon icon={faTimesCircle} className="status-icon error" />;
      case 'skipped':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon skipped" />;
      case 'paused':
        return <FontAwesomeIcon icon={faPause} style={{ color: '#f59e0b' }} />;
      case 'cancelled':
        return <FontAwesomeIcon icon={faBan} style={{ color: '#ef4444' }} />;
      case 'pending':
      default:
        return <FontAwesomeIcon icon={faClock} className="status-icon pending" />;
    }
  };

  // Burp-style per-phase dots. Each phase is a small labeled pill coloured by
  // its state (pending/running/done/skipped/error).
  const PhaseDots = ({ phases }) => {
    if (!Array.isArray(phases) || phases.length === 0) {
      return <span style={{ color: darkMode ? '#6b7280' : '#9ca3af', fontSize: '12px' }}>—</span>;
    }
    return (
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {phases.map((ph, i) => {
          const c = PHASE_COLORS[ph.status] || PHASE_COLORS.pending;
          return (
            <span
              key={i}
              title={`${ph.label}: ${ph.status}`}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: '1px 6px', borderRadius: '999px', fontSize: '10px', fontWeight: 700,
                lineHeight: '14px', background: c.bg, color: c.fg,
                border: `1px solid ${c.border}`,
                animation: ph.status === 'running' ? 'pulse 1.2s ease-in-out infinite' : 'none'
              }}
            >
              {ph.label}
            </span>
          );
        })}
      </div>
    );
  };

  const getSeverityBadge = (severity, count) => {
    if (!count || count === 0 || severity === 'none') return null;

    const severityClass = {
      critical: 'severity-critical',
      high: 'severity-high',
      medium: 'severity-medium',
      low: 'severity-low',
      informational: 'severity-info'
    }[severity] || '';

    return (
      <span className={`severity-badge ${severityClass}`}>
        {count}
      </span>
    );
  };

  const styles = {
    container: {
      marginTop: '16px',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px',
      overflow: 'hidden',
      background: darkMode ? '#1f2937' : '#ffffff'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    thead: {
      background: darkMode ? '#111827' : '#f9fafb',
      borderBottom: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      color: darkMode ? '#9ca3af' : '#6b7280',
      whiteSpace: 'nowrap'
    },
    td: {
      padding: '12px 16px',
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      color: darkMode ? '#d1d5db' : '#374151'
    },
    row: {
      background: darkMode ? '#1f2937' : '#ffffff',
      transition: 'background-color 0.2s'
    },
    rowHover: {
      background: darkMode ? '#374151' : '#f9fafb'
    },
    emptyState: {
      padding: '48px',
      textAlign: 'center',
      color: darkMode ? '#9ca3af' : '#6b7280'
    }
  };

  if (!urlDetails || urlDetails.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          No URLs enumerated yet. The scan will populate this list once started.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>URL</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Phases</th>
              <th style={styles.th}>Insertion Points</th>
              <th style={styles.th}>Requests</th>
              <th style={styles.th}>Issues</th>
            </tr>
          </thead>
          <tbody>
            {urlDetails.map((urlDetail, index) => (
              <tr
                key={index}
                style={{ ...styles.row, cursor: scanActive ? 'context-menu' : 'default' }}
                onContextMenu={(e) => openRowMenu(e, urlDetail, index)}
                onMouseEnter={(e) => e.currentTarget.style.background = styles.rowHover.background}
                onMouseLeave={(e) => e.currentTarget.style.background = styles.row.background}
              >
                <td style={styles.td}>
                  <span style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontFamily: 'monospace' }}>
                    {index + 1}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={{
                    maxWidth: '400px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }} title={urlDetail.url}>
                    {urlDetail.url}
                  </div>
                  {urlDetail.error && (
                    <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                      {urlDetail.error}
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {getStatusIcon(urlDetail.status)}
                    <span style={{
                      fontSize: '13px',
                      textTransform: 'capitalize',
                      color: darkMode ? '#d1d5db' : '#374151'
                    }}>
                      {urlDetail.status || 'pending'}
                    </span>
                  </div>
                </td>
                <td style={styles.td}>
                  <PhaseDots phases={urlDetail.phases} />
                </td>
                <td style={styles.td}>
                  <span style={{
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    color: urlDetail.injectionPoints > 0
                      ? (darkMode ? '#60a5fa' : '#3b82f6')
                      : (darkMode ? '#6b7280' : '#9ca3af')
                  }}>
                    {urlDetail.injectionPoints || 0}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={{
                    fontFamily: 'monospace',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    {urlDetail.requestsSent || 0}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {urlDetail.findingsCount > 0 ? (
                      getSeverityBadge(urlDetail.highestSeverity, urlDetail.findingsCount)
                    ) : (
                      <span style={{ color: darkMode ? '#6b7280' : '#9ca3af', fontSize: '13px' }}>
                        -
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          isDarkMode={darkMode}
        />
      )}
    </div>
  );
};

export default ScanUrlTable;
